import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { useGoogleLogin } from '@react-oauth/google';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);
const AUTH_CACHE_KEY = 'fm_auth_user';

export function AuthProvider({ children }) {
  // Seed state from sessionStorage so loading=false on cached visits
  const cached = (() => {
    try { return JSON.parse(sessionStorage.getItem(AUTH_CACHE_KEY)); } catch { return null; }
  })();

  const [user, setUser] = useState(cached);
  const [loading, setLoading] = useState(!cached); // skip loading if cached
  const [loginError, setLoginError] = useState(null);

  const checkAuth = useCallback(async () => {
    try {
      const resp = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(resp.data);
      try { sessionStorage.setItem(AUTH_CACHE_KEY, JSON.stringify(resp.data)); } catch {}
    } catch {
      setUser(null);
      try { sessionStorage.removeItem(AUTH_CACHE_KEY); } catch {}
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Always revalidate in background to keep cache fresh
    checkAuth();
  }, [checkAuth]);

  const login = useGoogleLogin({
    onSuccess: async ({ access_token }) => {
      setLoginError(null);
      try {
        await axios.post(`${API}/auth/session`, { firebase_token: access_token }, { withCredentials: true });
        await checkAuth();
      } catch (err) {
        console.error('Login error:', err);
        const msg = err?.response?.data?.error || err?.message || 'Login failed. Please try again.';
        setLoginError(msg);
      }
    },
    onError: (err) => {
      console.error('Google login failed:', err);
      setLoginError('Google sign-in was cancelled or failed.');
    },
    flow: 'implicit',
  });

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (e) { /* ignore */ }
    setUser(null);
    try { sessionStorage.removeItem(AUTH_CACHE_KEY); } catch {}
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, logout, checkAuth, loginError }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
